import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Tables } from '../lib/database.types';

const QUERY_KEY = 'activities';

export const useActivities = (options = {}) => {
  const { 
    isPublished = true, 
    type = null,
    language = 'fr' 
  } = options;

  return useQuery({
    queryKey: [QUERY_KEY, { isPublished, type, language }],
    queryFn: async () => {
      let query = supabase
        .from(Tables.ACTIVITIES)
        .select(`
          *,
          translations:activities_translations(title, tagline, description, language_code)
        `)
        .order('display_order', { ascending: true });

      if (isPublished !== null) {
        query = query.eq('is_published', isPublished);
      }

      if (type) {
        query = query.eq('type', type);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data.map(activity => ({
        ...activity,
        title: activity.translations?.find(t => t.language_code === language)?.title || activity.translations?.[0]?.title,
        tagline: activity.translations?.find(t => t.language_code === language)?.tagline || activity.translations?.[0]?.tagline,
        description: activity.translations?.find(t => t.language_code === language)?.description || activity.translations?.[0]?.description,
      }));
    },
  });
};

export const useCreateActivity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (activity) => {
      const { data, error } = await supabase
        .from(Tables.ACTIVITIES)
        .insert(activity)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useUpdateActivity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }) => {
      const { data, error } = await supabase
        .from(Tables.ACTIVITIES)
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useDeleteActivity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from(Tables.ACTIVITIES)
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export default useActivities;
